import { useEffect, useState } from 'react';
import {
  UserPlus,
  Pencil,
  Trash2,
  Shield,
  User as UserIcon,
  Eye,
  Loader2,
  Users as UsersIcon,
  RefreshCw,
  Database,
} from 'lucide-react';
import { adminService, erpDataService } from './_shared/api';
import type { User, UserRole } from './_shared/types';
import UserFormModal from './_components/UserFormModal';

/**
 * Prospect > Settings — gestão de vendedores (ProspectUser) + limites de
 * agendamento por cidade. Acessível por SaasUser admin (auth híbrido
 * Fase 14a).
 *
 * NOTA: Esta tela cria/edita **vendedores em campo** (ProspectUser),
 * distinto dos SaasUsers do painel admin (gerenciados em
 * Configurações > Usuários).
 */
export default function ProspectSettings() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<User | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  async function load(): Promise<void> {
    setLoading(true);
    try {
      setUsers(await adminService.listUsers());
    } catch {
      alert('Erro ao carregar vendedores.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  function openNew(): void {
    setEditing(null);
    setModalOpen(true);
  }

  function openEdit(u: User): void {
    setEditing(u);
    setModalOpen(true);
  }

  async function handleDelete(u: User): Promise<void> {
    if (!confirm(`Remover "${u.name}"? Esta ação não pode ser desfeita.`)) return;
    setDeletingId(u.id);
    try {
      await adminService.deleteUser(u.id);
      await load();
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } }; message?: string };
      alert('Erro: ' + (e.response?.data?.message ?? e.message));
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-fg">Prospect — Settings</h1>
          <p className="text-sm text-fg-2">Vendedores em campo e parâmetros de agendamento</p>
        </div>
        <button
          onClick={openNew}
          className="flex items-center gap-2 h-10 px-4 bg-primary-solid hover:bg-primary-solid-hover rounded-xl text-white font-medium transition"
        >
          <UserPlus size={16} />
          <span className="hidden sm:inline">Novo vendedor</span>
        </button>
      </header>

      <section>
        <div className="flex items-center gap-2 mb-3 px-1">
          <UsersIcon size={14} className="text-primary" />
          <h2 className="text-xs font-bold uppercase tracking-wider text-primary">
            Vendedores ({users.length})
          </h2>
        </div>

        {loading ? (
          <div className="py-10 flex justify-center">
            <Loader2 size={24} className="animate-spin text-primary" />
          </div>
        ) : users.length === 0 ? (
          <p className="text-muted text-sm text-center py-8 bg-canvas border border-line rounded-2xl">
            Nenhum vendedor cadastrado.
          </p>
        ) : (
          <ul className="space-y-2.5">
            {users.map((u) => (
              <UserCard
                key={u.id}
                user={u}
                onEdit={() => openEdit(u)}
                onDelete={() => void handleDelete(u)}
                deleting={deletingId === u.id}
              />
            ))}
          </ul>
        )}
      </section>

      {/* Sincronização ERP */}
      <section className="bg-canvas border border-line rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <Database size={14} className="text-primary" />
          <h2 className="text-xs font-bold uppercase tracking-wider text-primary">
            Sincronização ERP
          </h2>
        </div>
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <p className="text-sm text-fg-2">
              Busca planos, vencimentos, POPs e vendedores do ERP e armazena no Mongo
              local. Roda toda vez que o catálogo do ERP muda.
            </p>
            <p className="text-xs text-muted mt-1">
              Usa as credenciais de <strong>Configurações → Token ERP</strong>.
            </p>
            {syncMessage && (
              <p
                className={`text-xs mt-2 ${
                  syncMessage.startsWith('Erro') ? 'text-danger' : 'text-success'
                }`}
              >
                {syncMessage}
              </p>
            )}
          </div>
          <button
            onClick={async () => {
              setSyncing(true);
              setSyncMessage(null);
              try {
                await erpDataService.sync();
                setSyncMessage('Sincronização concluída com sucesso.');
              } catch (err) {
                const e = err as { response?: { data?: { message?: string } }; message?: string };
                setSyncMessage(
                  'Erro: ' + (e.response?.data?.message ?? e.message ?? 'falha desconhecida'),
                );
              } finally {
                setSyncing(false);
              }
            }}
            disabled={syncing}
            className="flex items-center gap-2 h-10 px-4 bg-primary-solid hover:bg-primary-solid-hover disabled:bg-active rounded-xl text-white font-medium transition flex-shrink-0"
          >
            <RefreshCw size={14} className={syncing ? 'animate-spin' : ''} />
            {syncing ? 'Sincronizando…' : 'Sincronizar agora'}
          </button>
        </div>
      </section>

      {/* Vagas de agendamento: configuradas EXCLUSIVAMENTE em
          Agendamento > Configurações (fonte única — prospect, página
          pública e painel respeitam a mesma coleção). */}
      <p className="text-xs text-muted border border-line bg-surface rounded-xl px-4 py-3">
        As vagas de agendamento por cidade são configuradas em{' '}
        <span className="text-fg-2 font-medium">Agendamento &gt; Configurações</span> —
        o app prospect e a página pública respeitam esses limites automaticamente.
      </p>

      {modalOpen && (
        <UserFormModal
          editing={editing}
          onClose={() => setModalOpen(false)}
          onSaved={() => void load()}
        />
      )}
    </div>
  );
}

function UserCard({
  user,
  onEdit,
  onDelete,
  deleting,
}: {
  user: User;
  onEdit: () => void;
  onDelete: () => void;
  deleting: boolean;
}) {
  const visual = getRoleVisual(user.role);

  return (
    <li className="bg-canvas border border-line rounded-2xl p-4 flex items-center gap-3">
      <div
        className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${visual.avatarCls}`}
      >
        {visual.icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-fg truncate">{user.name}</div>
        <div className="text-xs text-fg-2 truncate">{user.email}</div>
        <div className="mt-1 flex items-center gap-2 flex-wrap">
          <span
            className={`text-xs font-medium px-2 py-0.5 rounded-full ${visual.badgeCls}`}
          >
            {visual.label}
          </span>
          {user.isActive === false && (
            <span className="text-xs text-danger bg-danger-soft/40 px-2 py-0.5 rounded-full">
              Inativo
            </span>
          )}
        </div>
      </div>
      <button onClick={onEdit} className="p-2 text-muted hover:text-fg-2" title="Editar">
        <Pencil size={16} />
      </button>
      <button
        onClick={onDelete}
        disabled={deleting}
        className="p-2 text-danger hover:text-danger disabled:opacity-50"
        title="Remover"
      >
        {deleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
      </button>
    </li>
  );
}

function getRoleVisual(role: UserRole) {
  if (role === 'admin') {
    return {
      avatarCls: 'bg-primary-solid',
      badgeCls: 'bg-primary-soft/50 text-primary',
      label: 'Admin',
      icon: <Shield size={18} className="text-white" />,
    };
  }
  if (role === 'supervisor') {
    return {
      avatarCls: 'bg-info-solid',
      badgeCls: 'bg-info-soft/50 text-info',
      label: 'Supervisor',
      icon: <Eye size={18} className="text-white" />,
    };
  }
  if (role === 'monitor') {
    return {
      avatarCls: 'bg-warning-solid',
      badgeCls: 'bg-warning-soft/50 text-warning',
      label: 'Monitor',
      icon: <Eye size={18} className="text-white" />,
    };
  }
  return {
    avatarCls: 'bg-faint',
    badgeCls: 'bg-hover text-fg-2',
    label: 'Vendedor',
    icon: <UserIcon size={18} className="text-white" />,
  };
}
